import type { UseQueryOptions } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import axios from "axios"
import { generalConfig, noCacheConfig } from "../config/general"
import { BATCH_SIZE } from "../constants/markets"
import QUERY_KEYS from "../constants/queryKeys"
import { ContractType } from "../enums/contract"
// Using native JavaScript sort instead of lodash
import { NetworkId } from "thales-utils"
import type { Ticket } from "../types/markets"
import type { NetworkConfig, SupportedNetwork } from "../types/network"
import { getContractInstance } from "../utils/contract"
import { updateTotalQuoteAndPayout } from "../utils/marketsV2"
import { Network } from "../enums/network"
import { mapTicket } from "../utils/tickets"
import type { ViemContract } from "../types/viem"

type TicketData = {
  ticketsData: any[]
  freeBetsData: any[]
  stakingBettingProxyData: any[]
}

type GamesInfoResponse = {
  data: any
}

type PlayersInfoResponse = {
  data: any
}

type LiveScoresResponse = {
  data: any
}

export const isTestNetwork = (networkId: SupportedNetwork): boolean => {
  return networkId === Network.OptimismSepolia
}

export const useUserTicketsQuery = (
  walletAddress: string,
  networkConfig: NetworkConfig,
  options?: Omit<UseQueryOptions<Ticket[] | null>, "queryKey" | "queryFn">
) => {
  return useQuery<Ticket[] | null>({
    queryKey: QUERY_KEYS.UserTickets(networkConfig.networkId, walletAddress),
    queryFn: async () => {
      let userTickets: Ticket[] | null = null

      try {
        const sportsAMMDataContract = getContractInstance(
          ContractType.SPORTS_AMM_DATA,
          networkConfig
        ) as ViemContract
        const sportsAMMV2ManagerContract = getContractInstance(
          ContractType.SPORTS_AMM_V2_MANAGER,
          networkConfig
        ) as ViemContract
        const freeBetHolderContract = getContractInstance(
          ContractType.FREE_BET_HOLDER,
          networkConfig
        ) as ViemContract
        const stakingThalesBettingProxy = getContractInstance(
          ContractType.STAKING_THALES_BETTING_PROXY,
          networkConfig
        ) as ViemContract

        if (
          sportsAMMDataContract &&
          sportsAMMV2ManagerContract &&
          freeBetHolderContract
        ) {
          // Parallel contract calls for better performance
          const [
            numOfActiveTicketsPerUser,
            numOfResolvedTicketsPerUser,
            numOfActiveFreeBetTicketsPerUser,
            numOfResolvedFreeBetTicketsPerUser,
            numOfActiveStakedThalesTicketsPerUser,
            numOfResolvedStakedThalesTicketsPerUser,
          ] = await Promise.all([
            sportsAMMV2ManagerContract.read.numOfActiveTicketsPerUser([
              walletAddress,
            ]),
            sportsAMMV2ManagerContract.read.numOfResolvedTicketsPerUser([
              walletAddress,
            ]),
            freeBetHolderContract.read.numOfActiveTicketsPerUser([
              walletAddress,
            ]),
            freeBetHolderContract.read.numOfResolvedTicketsPerUser([
              walletAddress,
            ]),
            networkConfig.networkId === NetworkId.Base
              ? Promise.resolve(0)
              : stakingThalesBettingProxy?.read.numOfActiveTicketsPerUser([
                  walletAddress,
                ]) || Promise.resolve(0),
            networkConfig.networkId === NetworkId.Base
              ? Promise.resolve(0)
              : stakingThalesBettingProxy?.read.numOfResolvedTicketsPerUser([
                  walletAddress,
                ]) || Promise.resolve(0),
          ])

          // Optimize batch calculations
          const maxActiveTickets = Math.max(
            Number(numOfActiveTicketsPerUser),
            Number(numOfActiveFreeBetTicketsPerUser),
            Number(numOfActiveStakedThalesTicketsPerUser)
          )

          const maxResolvedTickets = Math.max(
            Number(numOfResolvedTicketsPerUser),
            Number(numOfResolvedFreeBetTicketsPerUser),
            Number(numOfResolvedStakedThalesTicketsPerUser)
          )

          const numberOfActiveBatches = Math.ceil(maxActiveTickets / BATCH_SIZE)
          const numberOfResolvedBatches = Math.ceil(
            maxResolvedTickets / BATCH_SIZE
          )

          const playersInfoQueryParam = `isTestnet=${isTestNetwork(networkConfig.networkId)}`

          // Prepare all promises for parallel execution
          const promises: Promise<any>[] = []

          // Batch ticket data fetching
          for (let i = 0; i < numberOfActiveBatches; i++) {
            promises.push(
              sportsAMMDataContract.read.getActiveTicketsDataPerUser([
                walletAddress,
                i * BATCH_SIZE,
                BATCH_SIZE,
              ])
            )
          }

          for (let i = 0; i < numberOfResolvedBatches; i++) {
            promises.push(
              sportsAMMDataContract.read.getResolvedTicketsDataPerUser([
                walletAddress,
                i * BATCH_SIZE,
                BATCH_SIZE,
              ])
            )
          }

          // Add API calls to promises array
          promises.push(
            axios.get<GamesInfoResponse>(
              `${generalConfig.API_URL}/overtime-v2/games-info`,
              noCacheConfig
            )
          )
          promises.push(
            axios.get<PlayersInfoResponse>(
              `${generalConfig.API_URL}/overtime-v2/players-info?${playersInfoQueryParam}`,
              noCacheConfig
            )
          )
          promises.push(
            axios.get<LiveScoresResponse>(
              `${generalConfig.API_URL}/overtime-v2/live-scores`,
              noCacheConfig
            )
          )

          // Execute all promises in parallel
          const promisesResult = await Promise.all(promises)
          const promisesLength = promises.length

          // Extract ticket data (excluding the last 3 API responses)
          const tickets = promisesResult
            .slice(0, promisesLength - 3)
            .map((allData: TicketData) => [
              ...allData.ticketsData,
              ...allData.freeBetsData,
              ...allData.stakingBettingProxyData,
            ])
            .flat(1)

          // Extract API responses
          const gamesInfoResponse = promisesResult[
            promisesLength - 3
          ] as AxiosResponse<GamesInfoResponse>
          const playersInfoResponse = promisesResult[
            promisesLength - 2
          ] as AxiosResponse<PlayersInfoResponse>
          const liveScoresResponse = promisesResult[
            promisesLength - 1
          ] as AxiosResponse<LiveScoresResponse>

          // Process tickets efficiently
          const mappedTickets: Ticket[] = tickets.map((ticket: any) =>
            mapTicket(
              ticket,
              networkConfig.networkId,
              gamesInfoResponse.data,
              playersInfoResponse.data,
              liveScoresResponse.data
            )
          )

          // Sort and update tickets
          userTickets = updateTotalQuoteAndPayout(mappedTickets).sort(
            (a, b) => b.timestamp - a.timestamp
          )
        }
      } catch (e) {
        console.error("Error fetching user tickets:", e)
        // Return null on error to allow fallback handling
        return null
      }

      return userTickets
    },
    // Enhanced query options for better performance
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: 1000,
    ...options,
  })
}
